import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import { useTranslation } from "@i18n";
import type { RootState } from "@Reducers/index";
import { getBackendUrl } from "@Utils";
import axiosWrapper from "@Services/axiosWrapper";
import { showToastNotification } from "@Reducers/toastSlice";
import { TOAST_TYPE_ERROR } from "@Services/constants";
import { QueryBuilder, PredicateBuilder } from "@Services/queryBuilder";
import Views from "@Components/Views";
import ConfigureLayoutModal from "./ConfigureLayoutModal";
import {
  PROJECT_DASHBOARD_RESOURCE_TYPE,
  PROJECT_DASHBOARD_SELECTED_LAYOUT_KEY,
  LAYOUT_MENU_ACTION,
  ALL_MODULES_LAYOUT_CODE,
  SIDEBAR_LAYOUTS_TOGGLE_ATTR,
} from "@Helpers/constants";
import type { GetViewDataResponse, LayoutData } from "@ServerTypes/layoutView/layoutView.schema";
import type { DropdownItem } from "@Helpers/types/layout/types";
import type { SidebarIcon } from "./ConfigureLayoutModal";
interface ProjectLayoutsProps {
  onClose: () => void;
  /** "mid" position sidebar icons, used as the pool of available modules in the create modal. */
  midIcons: SidebarIcon[];
  projectId: string | null;
  /** Whether the sidebar is expanded — used to anchor the flyout just to its right. */
  isExpanded: boolean;
  /** _id of the currently applied layout (for highlighting in the list). */
  selectedLayoutId?: string;
  /** Called when a layout becomes active (clicked or just created) so the sidebar can apply it. */
  onLayoutSelected: (layout: LayoutData) => void;
}

function ProjectLayouts({
  onClose,
  midIcons,
  projectId,
  isExpanded,
  selectedLayoutId,
  onLayoutSelected,
}: ProjectLayoutsProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const currentUserId = useSelector((state: RootState) => state.userDetails._id);
  const flyoutRef = useRef<HTMLDivElement>(null);
  const [layoutData, setLayoutData] = useState<LayoutData[]>([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createShared, setCreateShared] = useState(false);
  // The layout currently being configured (edit mode); null when creating / closed.
  const [editLayout, setEditLayout] = useState<LayoutData | null>(null);

  const isModalOpen = showCreateModal || editLayout !== null;

  useEffect(() => {
    if (!projectId) return;
    let active = true;
    // Declared then called rather than written as an IIFE: TypeScript inlines an IIFE's control
    // flow, so it narrows the captured `active` to `true` and the stale-response guard below reads
    // as dead code. Behind a named function it is correctly seen as a mutable captured boolean.
    const loadLayouts = async () => {
      try {
        const query = new QueryBuilder().where(
          new PredicateBuilder("resourceType", "equal", PROJECT_DASHBOARD_RESOURCE_TYPE),
        );
        const response = await axiosWrapper.post<GetViewDataResponse>(`${getBackendUrl()}/layoutView/getViewData`, {
          value: query.build(),
          projectId,
        });
        // Ignore a response that resolves after projectId changed, so a slow fetch for the previous
        // project can't overwrite the current project's layouts (mirrors StickySidebar's active flag).
        if (!active) return;
        if (response.data.status) setLayoutData(response.data.data ?? []);
      } catch {
        // Leave the current list on a transient fetch failure; the flyout is a secondary surface and
        // the original path already ignored non-success responses silently.
      }
    };
    void loadLayouts();
    return () => {
      active = false;
    };
  }, [projectId]);

  // Close the flyout on an outside click. A document listener (instead of a full-screen
  // overlay) avoids intercepting scroll/pointer events over the dashboard behind it.
  useEffect(() => {
    // While a modal is open, let it own dismissal — don't close the flyout underneath it.
    if (isModalOpen) return;
    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      // Click inside the flyout itself — keep open.
      if (flyoutRef.current && flyoutRef.current.contains(target)) return;
      // The Layouts sidebar icon owns its own toggle (open/close). Let its onClick handle it,
      // otherwise this mousedown closes the flyout right before the toggle reopens it.
      if (target.closest(`[${SIDEBAR_LAYOUTS_TOGGLE_ATTR}]`)) return;
      // The row menus and the create menu render as MUI Poppers, and modals as MUI Dialogs,
      if (target.closest(".MuiPopper-root, .MuiModal-root, .MuiDialog-root, .MuiBackdrop-root")) return;
      onClose();
    };
    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [isModalOpen, onClose]);

  // Persist the selected layout for this user/project and apply it to the sidebar.
  async function selectLayout(layout: LayoutData) {
    if (!layout._id) return;
    onLayoutSelected(layout);
    try {
      await axiosWrapper.post(`${getBackendUrl()}/layoutView/setSelectedViewerLayout`, {
        selectedViewerId: layout._id,
        key: PROJECT_DASHBOARD_SELECTED_LAYOUT_KEY,
        projectId,
      });
    } catch {
      // The layout is already applied in the UI; only persistence failed — tell the user so it isn't
      // a silent no-op that reverts on the next reload.
      dispatch(showToastNotification({ content: t("Something went wrong"), type: TOAST_TYPE_ERROR }));
    }
  }

  const closeModal = () => {
    setShowCreateModal(false);
    setEditLayout(null);
  };

  // Per-row menu: "my" layouts can be moved to shared / duplicated; shared layouts can be copied to "my".
  // Configure/Delete/Move-to-Shared go through ownership-scoped endpoints, so they only work on layouts
  // this user created. Seeded defaults (createdBy = system) and other users' shared layouts are never
  // owned here, so we hide those actions on them — otherwise the service returns status:false and the UI
  // silently no-ops. Duplicate / Duplicate as My always create a fresh owned copy, so they stay.
  const getDropDownItems = (item: LayoutData): DropdownItem[] => {
    const configure = { text: t("Configure"), id: LAYOUT_MENU_ACTION.EDIT, iconName: "SettingsIcon" };
    const del = { text: t("Delete"), id: LAYOUT_MENU_ACTION.DELETE, iconName: "delete" };
    const isOwner = !!currentUserId && item.createdBy === currentUserId && !item.isDefault;
    if (item.isShared) {
      return [
        ...(isOwner ? [configure] : []),
        { text: t("Duplicate as My Layout"), id: LAYOUT_MENU_ACTION.DUPLICATE_AS_MY, iconName: "copy" },
        ...(isOwner ? [del] : []),
      ];
    }
    return [
      ...(isOwner
        ? [configure, { text: t("Move to Shared Layout"), id: LAYOUT_MENU_ACTION.MOVE_TO_SHARED, iconName: "copy" }]
        : []),
      { text: t("Duplicate"), id: LAYOUT_MENU_ACTION.DUPLICATE, iconName: "copy" },
      ...(isOwner ? [del] : []),
    ];
  };

  const handleSaved = (layout: LayoutData, isEdit: boolean) => {
    if (isEdit) {
      setLayoutData((prev) => prev.map((l) => (l._id === layout._id ? { ...l, ...layout } : l)));
      if (layout._id === selectedLayoutId) {
        selectLayout(layout);
      }
      closeModal();
      return;
    }
    setLayoutData((prev) => [...prev, layout]);
    closeModal();
    selectLayout(layout);
    onClose();
  };

  return (
    <>
      <div
        ref={flyoutRef}
        className="fixed bottom-4 z-1201 flex h-124 max-h-[calc(100vh-2rem)] w-126 flex-col overflow-auto rounded-lg bg-white p-6 text-(--primary-text) shadow-[0_0_0.9375rem_0_rgba(65,92,171,0.25)]"
        style={{ insetInlineStart: isExpanded ? "16rem" : "5rem" }}
        data-testid="layoutsFlyout"
      >
        <Views
          layoutData={layoutData}
          setLayoutData={setLayoutData}
          currentDataId={selectedLayoutId}
          getDropDownItems={getDropDownItems}
          onEditCallback={({ layoutData: layoutToConfigure }: { layoutData: LayoutData }) =>
            setEditLayout(layoutToConfigure)
          }
          onClickLayout={(layout: LayoutData) => selectLayout(layout)}
          // Deleting the active layout would leave redux + user_settings pointing at a now soft-deleted
          // row. Re-point the selection to the "All Modules" default so nothing dangles.
          onDeleteCallback={({ deleteData }: { deleteData: LayoutData }) => {
            if (deleteData._id !== selectedLayoutId) return;
            const allModules = layoutData.find((layout) => layout.code === ALL_MODULES_LAYOUT_CODE);
            if (allModules) selectLayout(allModules);
          }}
          createNewLayoutData={() => ({})}
          onCreateClick={(isShared) => {
            setCreateShared(isShared);
            setShowCreateModal(true);
          }}
        />
      </div>
      {isModalOpen && (
        <ConfigureLayoutModal
          midIcons={midIcons}
          projectId={projectId}
          isShared={createShared}
          layoutToEdit={editLayout ?? undefined}
          onClose={closeModal}
          onSaved={handleSaved}
        />
      )}
    </>
  );
}

export default ProjectLayouts;
